import { useMutation } from '@tanstack/react-query'
import { createApiCall } from 'services'

import type { Permissions } from 'types'

// ---- Types ---------------------------------------------------------------

export type VeraGrantResponse = {
  companyName: string
  permissions: Permissions[]
}

type EnterSpaceInput = {
  companyId: string
}

type ExitSpaceInput = {
  companyId: string
}

type UseVeraSpaceMutationsOptions = {
  onEnterSuccess?: (data: VeraGrantResponse, companyId: string) => void
  onEnterError?: (error?: any) => void
  onExitSuccess?: () => void
  onExitError?: (error?: any) => void
}

type UseVeraSpaceMutationsResult = {
  /** Fetch the company's vera_support grant and enter space mode. */
  enterAsync: (input: EnterSpaceInput) => Promise<VeraGrantResponse>
  isEntering: boolean
  isEnterError: boolean

  /** POST exit-space (best-effort audit log) then clear space state. */
  exitAsync: (input: ExitSpaceInput) => Promise<{ ok: boolean }>
  isExiting: boolean
  isExitError: boolean
}

// ---- Hook ----------------------------------------------------------------

export const useVeraSpaceMutationsQuery = (
  options?: UseVeraSpaceMutationsOptions,
): UseVeraSpaceMutationsResult => {
  const enterMutation = useMutation({
    mutationFn: async (input: EnterSpaceInput): Promise<VeraGrantResponse> => {
      const response = await createApiCall({
        method: 'GET',
        url: `/internal/companies/${input.companyId}/vera-grant`,
      })
      if (!response?.data) {
        throw new Error(response?.data?.message ?? 'Failed to fetch vera grant')
      }
      return response.data as VeraGrantResponse
    },
    onSuccess: (data, variables) => {
      options?.onEnterSuccess?.(data, variables.companyId)
    },
    onError: (error) => {
      options?.onEnterError?.(error)
    },
  })

  const exitMutation = useMutation({
    mutationFn: async (input: ExitSpaceInput): Promise<{ ok: boolean }> => {
      const response = await createApiCall({
        method: 'POST',
        url: `/internal/companies/${input.companyId}/exit-space`,
      })
      // Best-effort: don't throw on error — we always exit locally
      return (response?.data as { ok: boolean }) ?? { ok: true }
    },
    onSuccess: () => {
      options?.onExitSuccess?.()
    },
    onError: (error) => {
      options?.onExitError?.(error)
    },
  })

  return {
    enterAsync: enterMutation.mutateAsync,
    isEntering: enterMutation.isPending,
    isEnterError: enterMutation.isError,

    exitAsync: exitMutation.mutateAsync,
    isExiting: exitMutation.isPending,
    isExitError: exitMutation.isError,
  }
}
